import { useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Button } from "@/components/ui/button";

// Only verified bytes enter PDF.js. No document URLs, annotation layer, forms,
// scripting sandbox or document actions are exposed to the page.
export function PdfPreview({ bytes }: { bytes: Uint8Array }) {
  const [document, setDocument] = useState<PDFDocumentProxy>();
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  useEffect(() => {
    let disposed = false;
    let destroy: (() => void) | undefined;
    setDocument(undefined); setFailed(false); setPage(1);
    const timeout = window.setTimeout(() => { disposed = true; destroy?.(); setFailed(true); }, 30_000);
    void import("pdfjs-dist").then(async (pdfjs) => {
      if (disposed) return;
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      const task = pdfjs.getDocument({
        data: bytes.slice(), // Worker transfer must not detach download bytes.
        enableXfa: false, useWorkerFetch: false, useWasm: false,
        stopAtErrors: true, maxImageSize: 16_000_000,
      });
      destroy = () => { void task.destroy().catch(() => undefined); };
      const loaded = await task.promise;
      if (!disposed) { window.clearTimeout(timeout); setDocument(loaded); }
    }).catch(() => {
      if (!disposed) { window.clearTimeout(timeout); destroy?.(); setFailed(true); }
    });
    return () => { disposed = true; window.clearTimeout(timeout); destroy?.(); };
  }, [bytes]);
  if (failed) return <PreviewUnavailable />;
  if (!document) return <p className="mt-8 text-sm" role="status">Preparing PDF preview…</p>;
  return <section className="mt-8" aria-label="PDF preview">
    <div className="mb-5 flex flex-wrap items-center gap-3">
      <Button variant="outline" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous page</Button>
      <span className="text-sm" aria-live="polite">Page {page} of {document.numPages}</span>
      <Button variant="outline" disabled={page >= document.numPages} onClick={() => setPage((value) => value + 1)}>Next page</Button>
    </div>
    <PdfPage key={page} document={document} page={page} />
  </section>;
}

function PreviewUnavailable() {
  return <p className="mt-8 text-sm leading-6 text-[#52606e]" role="status">This PDF couldn't be previewed. Download the saved version to open it.</p>;
}

function PdfPage({ document, page }: { document: PDFDocumentProxy; page: number }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<{ text?: string; ready?: boolean; failed?: boolean }>({});
  useEffect(() => {
    let disposed = false;
    let cancel: (() => void) | undefined;
    let pageToClean: PDFPageProxy | undefined;
    const surface = canvas.current;
    const timeout = window.setTimeout(() => { disposed = true; cancel?.(); setState({failed:true}); }, 30_000);
    void document.getPage(page).then(async (pdfPage) => {
      pageToClean = pdfPage;
      if (disposed || !surface) return;
      const original = pdfPage.getViewport({scale:1});
      if (![original.width,original.height].every((value) => Number.isFinite(value) && value > 0)) throw new Error("Invalid page dimensions");
      const scale = Math.min(2, 1600 / original.width, 2000 / original.height);
      const viewport = pdfPage.getViewport({scale});
      surface.width = Math.ceil(viewport.width); surface.height = Math.ceil(viewport.height);
      // Paint saved annotation appearances, but create no interactive layer.
      const task = pdfPage.render({canvas:surface, viewport, annotationMode:1});
      cancel = () => task.cancel();
      await task.promise;
      const content = await pdfPage.getTextContent();
      if (!disposed) {
        const text = content.items.map((item) => "str" in item ? item.str : "").join(" ");
        window.clearTimeout(timeout); setState({ready:true,text});
      }
    }).catch(() => {
      if (!disposed) { window.clearTimeout(timeout); setState({failed:true}); }
    }).finally(() => { pageToClean?.cleanup(); });
    return () => {
      disposed = true; window.clearTimeout(timeout); cancel?.();
      if (surface) { surface.width = 0; surface.height = 0; }
    };
  }, [document,page]);
  return <>
    {state.failed ? <PreviewUnavailable /> : !state.ready ? <p role="status" className="text-sm">Rendering page…</p> : null}
    <canvas ref={canvas} className={`h-auto max-w-full ${state.ready ? "" : "hidden"}`} role="img" aria-label={`PDF page ${page}`} />
    {state.text ? <details className="mt-5 text-sm"><summary className="cursor-pointer underline">Page text</summary><p className="mt-3 whitespace-pre-wrap break-words leading-7">{state.text}</p></details> : null}
  </>;
}
